import { CircularProgress } from '@mui/material';
import { renderProp, RenderPropType } from '@procureai/ui-mui';
import { useEffect, useCallback, useRef } from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

import { LoadingContainer } from './VirtualListDynamicHeight.styled';

const PADDING_SIZE = 20;

const defaultGetKey = (item: any) => {
  return typeof item === 'object' ? item.id : item;
};

export interface VirtualListDynamicHeightProps<I> {
  renderItem: RenderPropType<{ item: NonNullable<I>; virtual?: boolean; index?: number }>;
  items: I[];
  loading?: boolean;
  itemSize: number;
  width?: number | string;
  height?: number | string;
  getKey?: (item: I) => string | number;
}

export interface VirtualListRowProps<I> extends ListChildComponentProps<I> {
  renderItem: RenderPropType<{ item: NonNullable<I>; virtual?: boolean; index?: number }>;
  updateSize: (index: number, size: number) => void;
  getKey: (item: I) => string | number;
}

const VirtualListRow = <I,>({ data, index, renderItem, updateSize, style, getKey }: VirtualListRowProps<I>) => {
  const rowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (rowRef.current) {
      const h = rowRef.current.getBoundingClientRect().height;
      updateSize(index, h);
    }
  }, [updateSize, index]);

  return data ? (
    <div
      style={{
        ...style,
        top: `${parseFloat(style.top as any) + PADDING_SIZE}px`,
        left: PADDING_SIZE,
        right: PADDING_SIZE,
        width: 'auto',
      }}
    >
      <div ref={rowRef}>{renderProp(renderItem, { key: getKey(data), item: data, index, virtual: true })}</div>
    </div>
  ) : null;
};

export const VirtualListDynamicHeight = <I,>({
  renderItem,
  items,
  loading,
  itemSize,
  width,
  height,
  getKey = defaultGetKey,
}: VirtualListDynamicHeightProps<I>) => {
  const listRef = useRef<any>(undefined);
  const sizeMap = useRef<{ [key: number]: number }>({});
  const setSize = useCallback((index: number, size: number) => {
    sizeMap.current = { ...sizeMap.current, [index]: size + PADDING_SIZE };
    listRef.current.resetAfterIndex(index);
  }, []);
  const getSize = (index: number) => sizeMap.current[index] || itemSize;

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  return renderItem ? (
    <VariableSizeList
      ref={listRef}
      itemCount={items.length}
      height={height || 400}
      width={width || 500}
      itemSize={getSize}
      itemData={items}
    >
      {(rowProps) => {
        return (
          <VirtualListRow
            {...rowProps}
            data={items[rowProps.index]}
            renderItem={renderItem}
            updateSize={setSize}
            getKey={getKey}
          />
        );
      }}
    </VariableSizeList>
  ) : null;
};
